/**
 * Sound effect types
 */
type SoundType = 'move' | 'dash' | 'attack' | 'heal' | 'defeat' | 'select' | 'victory' | 'coinFlip' | 'coinLand' | 'qrSuccess';

/**
 * Simple sound manager using Web Audio API for generated sounds
 */
class SoundManagerClass {
  private audioContext: AudioContext | null = null;
  private enabled = true;

  private getContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }
    return this.audioContext;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Play a sound effect
   */
  play(type: SoundType): void {
    if (!this.enabled) return;

    try {
      const ctx = this.getContext();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      switch (type) {
        case 'move':
          this.playTone(ctx, 300, 0.1, 'sine', 0.3);
          break;
        case 'dash':
          this.playTone(ctx, 400, 0.15, 'sawtooth', 0.2);
          setTimeout(() => this.playTone(ctx, 500, 0.1, 'sawtooth', 0.2), 50);
          break;
        case 'attack':
          this.playNoise(ctx, 0.15, 0.4);
          this.playTone(ctx, 150, 0.2, 'square', 0.3);
          break;
        case 'heal':
          this.playTone(ctx, 400, 0.1, 'sine', 0.2);
          setTimeout(() => this.playTone(ctx, 500, 0.1, 'sine', 0.2), 100);
          setTimeout(() => this.playTone(ctx, 600, 0.15, 'sine', 0.2), 200);
          break;
        case 'defeat':
          this.playTone(ctx, 200, 0.3, 'sawtooth', 0.3);
          setTimeout(() => this.playTone(ctx, 150, 0.3, 'sawtooth', 0.25), 150);
          setTimeout(() => this.playTone(ctx, 100, 0.4, 'sawtooth', 0.2), 300);
          break;
        case 'select':
          this.playTone(ctx, 500, 0.05, 'sine', 0.2);
          break;
        case 'victory':
          this.playTone(ctx, 400, 0.15, 'sine', 0.3);
          setTimeout(() => this.playTone(ctx, 500, 0.15, 'sine', 0.3), 150);
          setTimeout(() => this.playTone(ctx, 600, 0.15, 'sine', 0.3), 300);
          setTimeout(() => this.playTone(ctx, 800, 0.3, 'sine', 0.3), 450);
          break;
        case 'coinFlip':
          // Rapid clicking sounds like a spinning coin
          for (let i = 0; i < 8; i++) {
            setTimeout(() => this.playTone(ctx, 800 + Math.random() * 200, 0.03, 'sine', 0.15), i * 100);
          }
          for (let i = 8; i < 15; i++) {
            setTimeout(() => this.playTone(ctx, 700 + Math.random() * 150, 0.04, 'sine', 0.12), i * 120);
          }
          break;
        case 'coinLand':
          // Satisfying landing thud with a ring
          this.playTone(ctx, 200, 0.15, 'sine', 0.4);
          this.playTone(ctx, 1200, 0.3, 'sine', 0.2);
          setTimeout(() => this.playTone(ctx, 1000, 0.2, 'sine', 0.15), 50);
          break;
        case 'qrSuccess':
          // Quick ascending success chime
          this.playTone(ctx, 600, 0.1, 'sine', 0.25);
          setTimeout(() => this.playTone(ctx, 800, 0.1, 'sine', 0.25), 80);
          setTimeout(() => this.playTone(ctx, 1000, 0.15, 'sine', 0.3), 160);
          break;
      }
    } catch {
      // Audio not supported or blocked
    }
  }

  private playTone(
    ctx: AudioContext,
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number
  ): void {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

    gainNode.gain.setValueAtTime(volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  }

  private playNoise(ctx: AudioContext, duration: number, volume: number): void {
    const bufferSize = ctx.sampleRate * duration;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);

    noise.connect(gainNode);
    gainNode.connect(ctx.destination);

    noise.start(ctx.currentTime);
  }
}

export const SoundManager = new SoundManagerClass();
